import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  Activity, FileText, Phone, CheckCircle2, UserPlus, MessageSquare,
  ArrowRight, Upload, Clock, Loader2
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

const ICON_MAP = {
  task_created: CheckCircle2,
  task_completed: CheckCircle2,
  note_added: MessageSquare,
  call_logged: Phone,
  document_uploaded: Upload,
  lead_created: UserPlus,
  stage_changed: ArrowRight,
  client_created: UserPlus,
  default: Activity,
};

const COLOR_MAP = {
  task_created: 'bg-blue-100 text-blue-600',
  task_completed: 'bg-green-100 text-green-600',
  note_added: 'bg-purple-100 text-purple-600',
  call_logged: 'bg-amber-100 text-amber-600',
  document_uploaded: 'bg-indigo-100 text-indigo-600',
  lead_created: 'bg-emerald-100 text-emerald-600',
  stage_changed: 'bg-orange-100 text-orange-600',
  client_created: 'bg-teal-100 text-teal-600',
  default: 'bg-gray-100 text-gray-600',
};

export default function RecentActivityFeed({ limit = 15 }) {
  const { token } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await api.get(token, '/analytics/recent-activity');
      setActivities((res.data.activities || []).slice(0, limit));
    } catch (err) {
      console.error('Activity fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, limit]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  if (loading) {
    return (
      <div className="rounded-xl border border-[#E6E0D9] bg-white p-6">
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#4A3B32]" /></div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white overflow-hidden" data-testid="recent-activity-feed">
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#4A3B32]" />
            <h3 className="font-heading text-base text-[#2C2420]">Recent Activity</h3>
          </div>
          <Badge variant="outline" className="text-[10px]">{activities.length} items</Badge>
        </div>
      </div>

      <div className="px-5 pb-5 max-h-[400px] overflow-y-auto">
        {activities.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="w-8 h-8 text-[#D4CCC6] mx-auto mb-2" />
            <p className="text-sm text-[#5D524D]">No recent activity</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-[#E6E0D9]" />

            <div className="space-y-3">
              {activities.map((act, i) => {
                const type = act.type || act.action || 'default';
                const IconComp = ICON_MAP[type] || ICON_MAP.default;
                const colorClass = COLOR_MAP[type] || COLOR_MAP.default;
                const ts = act.timestamp || act.createdAt || '';

                return (
                  <div key={act.id || i} className="flex gap-3 relative" data-testid={`activity-item-${i}`}>
                    <div className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10", colorClass)}>
                      <IconComp className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className="text-sm text-[#2C2420]">
                        <span className="font-medium">{act.userName || act.user || ''}</span>
                        {' '}{act.description || act.title || type.replace('_', ' ')}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {act.clientName && (
                          <Badge className="text-[10px] bg-[#F0EBE6] text-[#4A3B32]">{act.clientName}</Badge>
                        )}
                        <span className="text-[10px] text-[#8C8078]">
                          {ts ? formatDistanceToNow(parseISO(ts), { addSuffix: true }) : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
